import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { doPUT } from "../../util/HttpUtil";

const ROW_OPTIONS = [
  {
    label: "20 Rows",
    value: 20,
  },
  {
    label: "40 Rows",
    value: 40,
  },
  {
    label: "50 Rows",
    value: 50,
  },
  {
    label: "100 Rows",
    value: 100,
  },
  {
    label: "200 Rows",
    value: 200,
  },
  {
    label: "400 Rows",
    value: 400,
  },
];

const Pagination = ({
  hidePageNumber,
  page,
  rowsPerPage = 20,
  total,
  onChange = () => {},
  uiPreference,
  style,
  hideRowSelector,
  paginationMaxLimit = 400,
  className = "",
  testId = "pagination",
}) => {
  useEffect(() => {
    if (rowsPerPage > paginationMaxLimit) onChange(1, paginationMaxLimit);
  }, [onChange, paginationMaxLimit, rowsPerPage]);

  const filteredData = useMemo(
    () => ROW_OPTIONS.filter((d) => d.value <= paginationMaxLimit),
    [paginationMaxLimit]
  );

  const changePage = (page, rowsPerPage) => {
    const totalPages = rowsPerPage && total > 0 ? Math.ceil(total / rowsPerPage) : 1;
    if (
      page > 0 &&
      page <= totalPages
    ) {
      onChange(page, rowsPerPage);
    }
  };

  const [showMenu, setMenu] = useState(false);
  const rowsMenuRef = useRef(null);
  const normalizedRowsPerPage = Number(rowsPerPage);
  const totalPages = normalizedRowsPerPage && total > 0 ? Math.ceil(total / normalizedRowsPerPage) : 1;
  const isPrevDisabled = page <= 1;
  const isNextDisabled = page >= totalPages;

  useEffect(() => {
    if (!showMenu) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!rowsMenuRef.current?.contains(event.target)) {
        setMenu(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [showMenu]);

  return (
    <div className={`rw-pagination ${className}`.trim()} style={style} data-testid={testId}>
      <div className="rw-pagination__content" data-testid={`${testId}-content`}>
        {!hideRowSelector ? (
        <div className="rw-pagination__rows" data-testid={`${testId}-rows`} ref={rowsMenuRef}>
          <button
            type="button"
            className={`rw-pagination__rows-toggle ${showMenu ? "is-open" : ""}`}
            onClick={(event) => {
              event.preventDefault();
              setMenu((current) => !current);
            }}
            aria-haspopup="listbox"
            aria-expanded={showMenu}
            data-testid={`${testId}-rows-toggle`}
          >
            <span>{rowsPerPage} Rows</span>
            <span className="bx bx-chevron-up rw-pagination__rows-icon" aria-hidden="true" />
          </button>
          {showMenu ? (
            <div className="rw-pagination__rows-menu" role="listbox" data-testid={`${testId}-rows-menu`}>
              {filteredData.map((d, key) => {
                const { label, value } = d;
                const selected = Number(value) === normalizedRowsPerPage;
                return (
                  <button
                    key={key}
                    type="button"
                    className={`rw-pagination__rows-option ${selected ? "is-selected" : ""}`}
                    data-testid={`${testId}-rows-option-${value}`}
                    role="option"
                    aria-selected={selected}
                    onClick={async (event) => {
                      event.preventDefault();
                      setMenu(false);
                      onChange(1, Number(value));
                      if (uiPreference) {
                        await doPUT("/api/ui-preference/update", { key: `${uiPreference}.rows`, value: `${value}` });
                      }
                    }}
                  >
                    <span>{label}</span>
                    {selected ? <span className="rw-pagination__rows-check" aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
        ) : null}
        {/* <div className="d-flex align align-items-center"> */}
        <div className="rw-pagination__summary" data-testid={`${testId}-summary`}>
          {!hideRowSelector ? "Showing " : ""}
          {total === 0 ? "0" : (page - 1) * normalizedRowsPerPage + 1} -{" "}
          {page * normalizedRowsPerPage > total
            ? total
            : page * normalizedRowsPerPage}{" "}
          {total > -1 ? " of " + total : ''}
        </div>
        <div className="rw-pagination__nav" data-testid={`${testId}-nav`}>
          <button
            type="button"
            className="rw-pagination__nav-btn"
            onClick={(e) => {
              e.preventDefault();
              changePage(page - 1, normalizedRowsPerPage);
            }}
            disabled={isPrevDisabled}
            aria-label="Previous page"
            data-testid={`${testId}-previous`}
          >
            <span className="uil-angle-left" aria-hidden="true" />
          </button>
          {!hidePageNumber ? (
          <div className="rw-pagination__page" data-testid={`${testId}-page`}>
            {page}
          </div>
          ) : null}
          <button
            type="button"
            className="rw-pagination__nav-btn"
            onClick={(e) => {
              e.preventDefault();
              changePage(page + 1, normalizedRowsPerPage);
            }}
            disabled={isNextDisabled}
            aria-label="Next page"
            data-testid={`${testId}-next`}
          >
            <span className="uil-angle-right" aria-hidden="true" />
          </button>
        </div>
        {/* </div> */}
      </div>
    </div>
  );
};

export default observer(Pagination);
